import { useEffect, useState } from "react";
import {
  LuLibrary,
  LuChevronLeft,
  LuChevronRight,
  LuMoreHorizontal,
} from "react-icons/lu";

import Preloader from "../../../app-components/preloader/Preloader";
import apiClient from "../../../api-services/ApiClient";
import { ToastContainer } from "react-toastify";
import { ExcelFile, ExcelSheet, ExcelColumn } from "react-xlsx-wrapper";
import { RiFileExcel2Line } from "react-icons/ri";

const AppInsight = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportType, setReportType] = useState("");

  const [userActivityReport, setUserActivityReport] = useState<any[]>([]);
  const [endpointSummary, setEndpointSummary] = useState<any[]>([]);
  const [userSummary, setUserSummary] = useState<any[]>([]);
  const [successRate, setSuccessRate] = useState<any[]>([]);

  // effect for get users list
  useEffect(() => {
    setLastPage(1);
    
    const params = {
      page: currentPage,
      OrderBy: "Descending",
      PageSize: pageSize,
      StartDate: startDate,
      EndDate: endDate,
    };

    switch (reportType) {
      case "UserActivityReport":
        getUserActivityReport(params);
        break;

      case "EndpointSummary":
        getEndpointSummary(params);
        break;

      case "AverageLatency":
        getAverageLatency(params);
        break;

      case "UserSummary":
        getUserSummary(params);
        break;

      case "SuccessRate":
        getSuccessRate(params);
        break;
    }
  }, [currentPage, pageSize, startDate, endDate, reportType]);

  // ----------------------------------
  const getUserActivityReport = async (params: any) => {
    try {
      setLoading(true);
      const res: any = await apiClient.get(
        "PerformanceMetrics/UserActivityReport",
        { params }
      );
      if (res && res.data) {
        setUserActivityReport(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  const getEndpointSummary = async (params: any) => {
    try {
      setLoading(true);
      const res: any = await apiClient.get(
        "PerformanceMetrics/endpointSummary",
        { params }
      );
      if (res && res.data) {
        setEndpointSummary(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAverageLatency = async (params: any) => {
    try {
      const res: any = await apiClient.get(
        "PerformanceMetrics/averageLatency",
        { params }
      );
      if (res && res.data) {
        setUserActivityReport(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const getUserSummary = async (params: any) => {
    try {
      const res: any = await apiClient.get("PerformanceMetrics/userSummary", {
        params,
      });
      if (res && res.data) {
        setUserSummary(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const getSuccessRate = async (params: any) => {
    try {
      const res: any = await apiClient.get("PerformanceMetrics/successRate", {
        params,
      });
      if (res && res.data) {
        setSuccessRate(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < lastPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      {/* page lead */}
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-4 pb-24 flex justify-between items-center">
          <h4 className="lg:text-[16px] text-white font-medium">
            Application Insight
          </h4>

          {/*page actions */}
          <div className="flex gap-2">
            {reportType === "UserActivityReport" && (
              <ExcelFile
                filename={"User-Activity-Data-" + new Date()}
                element={
                  <button className="border-white border-2 flex gap-2 p-2 px-4 rounded-md text-sm items-center text-white font-medium hover:shadow-xl shadow">
                    <RiFileExcel2Line />
                    <span className="text-xs">Export Table</span>
                  </button>
                }
              >
                <ExcelSheet
                  data={userActivityReport}
                  name="User Activity Report"
                >
                  <ExcelColumn label="ID" value="userId" />
                  <ExcelColumn label="User ID" value="userId" />
                  <ExcelColumn label="activity Type" value="activityType" />
                  <ExcelColumn label="details" value="details" />
                  <ExcelColumn label="activityTime" value="activityTime" />
                </ExcelSheet>
              </ExcelFile>
            )}

            {reportType === "EndpointSummary" && (
              <ExcelFile
                filename={"User-endpointSummary-Data-" + new Date()}
                element={
                  <button className="border-white border-2 flex gap-2 p-2 px-4 rounded-md text-sm items-center text-white font-medium hover:shadow-xl shadow">
                    <RiFileExcel2Line />
                    <span className="text-xs">Export Endpoint Summary</span>
                  </button>
                }
              >
                <ExcelSheet data={endpointSummary} name="User Endpoint Report">
                  <ExcelColumn
                    label="Average Latency Ms"
                    value="averageLatencyMs"
                  />
                  <ExcelColumn label="Endpoint" value="endpoint" />
                  <ExcelColumn label="Success Rate" value="successRate" />
                  <ExcelColumn label="Total Requests" value="totalRequests" />
                </ExcelSheet>
              </ExcelFile>
            )}

            {reportType === "UserSummary" && (
              <ExcelFile
                filename={"User-endpointSummary-Data-" + new Date()}
                element={
                  <button className="border-white border-2 flex gap-2 p-2 px-4 rounded-md text-sm items-center text-white font-medium hover:shadow-xl shadow">
                    <RiFileExcel2Line />
                    <span className="text-xs">Export Endpoint Summary</span>
                  </button>
                }
              >
                <ExcelSheet data={userSummary} name="User Endpoint Report">
                  <ExcelColumn
                    label="Average Latency Ms"
                    value="averageLatencyMs"
                  />
                  <ExcelColumn label="userId" value="userId" />
                  <ExcelColumn label="Success Rate" value="successRate" />
                  <ExcelColumn label="Total Requests" value="totalRequests" />
                  <ExcelColumn label="Username" value="username" />
                </ExcelSheet>
              </ExcelFile>
            )}

            {reportType === "SuccessRate" && (
              <ExcelFile
                filename={"User-endpointSummary-Data-" + new Date()}
                element={
                  <button className="border-white border-2 flex gap-2 p-2 px-4 rounded-md text-sm items-center text-white font-medium hover:shadow-xl shadow">
                    <RiFileExcel2Line />
                    <span className="text-xs">Export Endpoint Summary</span>
                  </button>
                }
              >
                <ExcelSheet data={successRate} name="User Endpoint Report">
                  <ExcelColumn
                    label="Average Latency Ms"
                    value="averageLatencyMs"
                  />
                  <ExcelColumn label="userId" value="userId" />
                  <ExcelColumn label="Success Rate" value="successRate" />
                  <ExcelColumn label="Total Requests" value="totalRequests" />
                  <ExcelColumn label="Username" value="username" />
                </ExcelSheet>
              </ExcelFile>
            )}
          </div>
        </div>
      </section>

      {/* page content */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-100px]">
        <div className="rounded-lg bg-white shadow-sm flex p-10 gap-3">
          <select
            className="p-2 py-5 text-xs border border-gray-300 hover:border-blue-400 rounded-lg w-full font-semibold"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            <option value="">Report Title</option>
            <option value="UserActivityReport">User Activity Report</option>
            <option value="EndpointSummary">Endpoint Summary</option>
            <option value="AverageLatency">Average Latency</option>
            <option value="UserSummary">User Summary</option>
            <option value="SuccessRate">Success Rate</option>
          </select>

          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            id="entries"
            className="p-2 py-1.5 text-xs border border-gray-300 hover:border-blue-400 rounded-lg"
          >
            <option value="10">Show 10 entries</option>
            <option value="25">Show 25 entries</option>
            <option value="50">Show 50 entries</option>
            <option value="100">Show 100 entries</option>
            <option value="250">Show 250 entries</option>
            <option value="500">Show 500 entries</option>
            <option value="1000">Show 1000 entries</option>
          </select>

          <input
            className="p-2 py-5 text-xs border border-gray-300 hover:border-blue-400 rounded-lg w-full font-semibold"
            type="date"
            onChange={(e) => setStartDate(e.target.value)}
          />

          <input
            className="p-2 py-5 text-xs border border-gray-300 hover:border-blue-400 rounded-lg w-full font-semibold"
            type="date"
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </section>

      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-100px]">
        {reportType === "UserActivityReport" && (
          <div className="rounded-lg pb-6 bg-white shadow-sm border">
            <div className="overflow-x-auto mb-5 overflow-y-auto">
              <table className="min-w-full bg-white mb-4">
                <thead>
                  <tr className="w-full bg-slate-100 text-slate-600 capitalize text-[12px] leading-normal border-b shadow-sm">
                    <th className="py-3 px-3 text-left font-medium">SN</th>
                    <th className="py-3 px-3 text-left font-medium">ID</th>
                    <th className="py-3 px-3 text-left font-medium">
                      Activity Type
                    </th>
                    <th className="py-3 px-3 text-left font-medium">
                      Activity Time
                    </th>
                    <th className="py-3 px-3 text-left font-medium">Details</th>
                    <th className="py-3 px-3 text-left font-medium">User ID</th>
                  </tr>
                </thead>

                {!!userActivityReport?.length && (
                  <tbody>
                    {userActivityReport?.map((data: any, i: number) => {
                      return (
                        <tr
                          key={data.id}
                          className={`border-b border-[#F2F1F1] hover:bg-gray-100 font-normal text-xs`}
                        >
                          <td className="py-3 px-3 text-left align-top">
                            {i + 1}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.id}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.activityType}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.activityTime}
                          </td>

                          <td className="py-3 px-3 text-left break-words max-w-[250px] align-top cursor-pointer">
                            {data?.details}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.userId}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
              </table>

              {!userActivityReport?.length && (
                <div className="text-center p-5 text-sm text-gray-500 ">
                  <LuLibrary className="text-[50px] w-full" />
                  <p> No record found</p>
                </div>
              )}
            </div>

            <div className="flex justify-center gap-3 flex-wrap hidden">
              {/* Previous Button */}
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                  currentPage === 1
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <span>Previous</span>
              </button>

              {/* Left Arrow */}
              <button
                disabled={currentPage === 1}
                onClick={handlePrevious}
                className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-medium ${
                  currentPage === 1
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <LuChevronLeft className="text-xl" />
              </button>

              {/* Page Numbers */}
              {Array.from(
                { length: lastPage > 7 ? 3 : lastPage },
                (_, i) => i + 1
              ).map((page) => (
                <button
                  title={`page ${page}`}
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal text-xs ${
                    page === currentPage
                      ? "bg-red-600 text-white"
                      : "bg-[#fff] text-gray-600"
                  }`}
                >
                  <span>{page}</span>
                </button>
              ))}

              {lastPage > 7 && (
                <>
                  {/* ellipse Arrow */}
                  <button
                    disabled
                    className={`bg-[#fff] text-gray-600 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1]`}
                  >
                    <LuMoreHorizontal className="text-xl" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: 3 }, (_, i) => 2 - i).map((page) => (
                    <button
                      title={`page ${lastPage - page}`}
                      key={lastPage - page}
                      onClick={() => setCurrentPage(lastPage - page)}
                      className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-normal text-xs ${
                        lastPage - page === currentPage
                          ? "bg-red-600 text-white"
                          : "bg-[#fff] text-gray-600"
                      }`}
                    >
                      <span>{lastPage - page}</span>
                    </button>
                  ))}
                </>
              )}

              {/* Right Arrow */}
              <button
                disabled={currentPage === lastPage}
                onClick={handleNext}
                className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] ${
                  currentPage === lastPage
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <LuChevronRight className="text-xl" />
              </button>

              {/* Next Button */}
              <button
                onClick={handleNext}
                disabled={currentPage === lastPage}
                className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                  currentPage === lastPage
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <span>Next</span>
              </button>
            </div>
          </div>
        )}

        {reportType === "EndpointSummary" && (
          <div className="rounded-lg pb-6 bg-white shadow-sm border">
            <div className="overflow-x-auto mb-5 overflow-y-auto">
              <table className="min-w-full bg-white mb-4">
                <thead>
                  <tr className="w-full bg-slate-100 text-slate-600 capitalize text-[12px] leading-normal border-b shadow-sm">
                    <th className="py-3 px-3 text-left font-medium">SN</th>
                    <th className="py-3 px-3 text-left font-medium">
                      Average Latency Ms
                    </th>
                    <th className="py-3 px-3 text-left font-medium">
                      Endpoint
                    </th>
                    <th className="py-3 px-3 text-left font-medium">
                      successRate
                    </th>
                    <th className="py-3 px-3 text-left font-medium">
                      Total Requests
                    </th>
                  </tr>
                </thead>

                {!!endpointSummary?.length && (
                  <tbody>
                    {endpointSummary?.map((data: any, i: number) => {
                      return (
                        <tr
                          key={data.id}
                          className={`border-b border-[#F2F1F1] hover:bg-gray-100 font-normal text-xs`}
                        >
                          <td className="py-3 px-3 text-left align-top">
                            {i + 1}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.averageLatencyMs}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.endpoint}
                          </td>

                          <td className="py-3 px-3 text-left break-words max-w-[250px] align-top cursor-pointer">
                            {data?.successRate ? "Pass" : "Fail"}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.totalRequests}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
              </table>

              {!endpointSummary?.length && (
                <div className="text-center p-5 text-sm text-gray-500 ">
                  <LuLibrary className="text-[50px] w-full" />
                  <p> No record found</p>
                </div>
              )}
            </div>

            <div className="flex justify-center gap-3 flex-wrap hidden">
              {/* Previous Button */}
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                  currentPage === 1
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <span>Previous</span>
              </button>

              {/* Left Arrow */}
              <button
                disabled={currentPage === 1}
                onClick={handlePrevious}
                className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-medium ${
                  currentPage === 1
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <LuChevronLeft className="text-xl" />
              </button>

              {/* Page Numbers */}
              {Array.from(
                { length: lastPage > 7 ? 3 : lastPage },
                (_, i) => i + 1
              ).map((page) => (
                <button
                  title={`page ${page}`}
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal text-xs ${
                    page === currentPage
                      ? "bg-red-600 text-white"
                      : "bg-[#fff] text-gray-600"
                  }`}
                >
                  <span>{page}</span>
                </button>
              ))}

              {lastPage > 7 && (
                <>
                  {/* ellipse Arrow */}
                  <button
                    disabled
                    className={`bg-[#fff] text-gray-600 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1]`}
                  >
                    <LuMoreHorizontal className="text-xl" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: 3 }, (_, i) => 2 - i).map((page) => (
                    <button
                      title={`page ${lastPage - page}`}
                      key={lastPage - page}
                      onClick={() => setCurrentPage(lastPage - page)}
                      className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-normal text-xs ${
                        lastPage - page === currentPage
                          ? "bg-red-600 text-white"
                          : "bg-[#fff] text-gray-600"
                      }`}
                    >
                      <span>{lastPage - page}</span>
                    </button>
                  ))}
                </>
              )}

              {/* Right Arrow */}
              <button
                disabled={currentPage === lastPage}
                onClick={handleNext}
                className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] ${
                  currentPage === lastPage
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <LuChevronRight className="text-xl" />
              </button>

              {/* Next Button */}
              <button
                onClick={handleNext}
                disabled={currentPage === lastPage}
                className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                  currentPage === lastPage
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <span>Next</span>
              </button>
            </div>
          </div>
        )}

        {reportType === "UserSummary" && (
          <div className="rounded-lg pb-6 bg-white shadow-sm border">
            <div className="overflow-x-auto mb-5 overflow-y-auto">
              <table className="min-w-full bg-white mb-4">
                <thead>
                  <tr className="w-full bg-slate-100 text-slate-600 capitalize text-[12px] leading-normal border-b shadow-sm">
                    <th className="py-3 px-3 text-left font-medium">SN</th>
                    <th className="py-3 px-3 text-left font-medium">
                      Average Latency Ms
                    </th>
                    <th className="py-3 px-3 text-left font-medium">UserId</th>
                    <th className="py-3 px-3 text-left font-medium">
                      successRate
                    </th>
                    <th className="py-3 px-3 text-left font-medium">
                      Total Requests
                    </th>
                    <th className="py-3 px-3 text-left font-medium">
                      Username
                    </th>
                  </tr>
                </thead>

                {!!userSummary?.length && (
                  <tbody>
                    {userSummary?.map((data: any, i: number) => {
                      return (
                        <tr
                          key={data.id}
                          className={`border-b border-[#F2F1F1] hover:bg-gray-100 font-normal text-xs`}
                        >
                          <td className="py-3 px-3 text-left align-top">
                            {i + 1}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.averageLatencyMs}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.userId}
                          </td>

                          <td className="py-3 px-3 text-left break-words max-w-[250px] align-top cursor-pointer">
                            {data?.successRate ? "Pass" : "Fail"}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.totalRequests}
                          </td>

                          <td className="py-3 px-3 text-left align-top">
                            {data?.username}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
              </table>

              {!userSummary?.length && (
                <div className="text-center p-5 text-sm text-gray-500 ">
                  <LuLibrary className="text-[50px] w-full" />
                  <p> No record found</p>
                </div>
              )}
            </div>

            <div className="flex justify-center gap-3 flex-wrap hidden">
              {/* Previous Button */}
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                  currentPage === 1
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <span>Previous</span>
              </button>

              {/* Left Arrow */}
              <button
                disabled={currentPage === 1}
                onClick={handlePrevious}
                className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-medium ${
                  currentPage === 1
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <LuChevronLeft className="text-xl" />
              </button>

              {/* Page Numbers */}
              {Array.from(
                { length: lastPage > 7 ? 3 : lastPage },
                (_, i) => i + 1
              ).map((page) => (
                <button
                  title={`page ${page}`}
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal text-xs ${
                    page === currentPage
                      ? "bg-red-600 text-white"
                      : "bg-[#fff] text-gray-600"
                  }`}
                >
                  <span>{page}</span>
                </button>
              ))}

              {lastPage > 7 && (
                <>
                  {/* ellipse Arrow */}
                  <button
                    disabled
                    className={`bg-[#fff] text-gray-600 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1]`}
                  >
                    <LuMoreHorizontal className="text-xl" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: 3 }, (_, i) => 2 - i).map((page) => (
                    <button
                      title={`page ${lastPage - page}`}
                      key={lastPage - page}
                      onClick={() => setCurrentPage(lastPage - page)}
                      className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-normal text-xs ${
                        lastPage - page === currentPage
                          ? "bg-red-600 text-white"
                          : "bg-[#fff] text-gray-600"
                      }`}
                    >
                      <span>{lastPage - page}</span>
                    </button>
                  ))}
                </>
              )}

              {/* Right Arrow */}
              <button
                disabled={currentPage === lastPage}
                onClick={handleNext}
                className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] ${
                  currentPage === lastPage
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <LuChevronRight className="text-xl" />
              </button>

              {/* Next Button */}
              <button
                onClick={handleNext}
                disabled={currentPage === lastPage}
                className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                  currentPage === lastPage
                    ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                    : ""
                }`}
              >
                <span>Next</span>
              </button>
            </div>
          </div>
        )}
      </section>

      <ToastContainer position="bottom-left" hideProgressBar />
      <Preloader loading={loading} />
    </>
  );
};

export default AppInsight;
